/* eslint-disable max-lines */
import {createGAEvent} from '@optimaxdev/analytics/desktop/lib/gaEvent/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';

import {SagaIterator} from 'redux-saga';
import {call, select, put, takeEvery, getContext} from 'redux-saga/effects';

import {
    CHECKOUT_PAYMENT_STEP,
    CHECKOUT_PLACE_ORDER_PAYMENT_STEP,
} from 'constants/analytics/events/checkout';
import {OrderErrorType} from 'constants/constants';
import {ENDPOINTS} from 'constants/endpoints';
import {
    CHECKBOX_STATUS,
    PAYMENT_CODE_MESSAGE,
    PAYPAL_ORDER_SUCCESS_STATUS,
    STEPS,
    SUBSCRIBER_BY_PHONE,
} from 'desktop/pages/checkout/constants/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {checkAmazonEvent} from 'desktop/pages/checkout/utils/newsletterSubscription/analytics';
import {generateOneTimeCoupon} from 'desktop/pages/success/store/reducers/oneTimeCoupon/oneTimeCoupon';
import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';
import {apiConfig} from 'libs/api/api';
import {flags} from 'libs/rollout';
import {logWithSentry} from 'libs/sentry/sentry';
import {cookieStore} from 'libs/storage/storage';

import {ActionType} from 'types/reducers';

import {
    completeOrderReview,
    completeOrderReviewPaypal,
} from '../../actions/completeOrder/completeOrder';
import {resaveDelivery} from '../../reducers/checkout/checkout';
import {startSetCheckoutStepSaga} from '../../reducers/sagaStep/sagaStep';
import {getShippingEmail, getShippingPhoneNumber} from '../../selectors/checkout/checkout';
import {getCompleteOrderResult} from '../../selectors/completeOrder/completeOrder';
import {
    getSmsSubscriptionCheckboxState,
    getSmsSubscriptionCheckboxStatus,
} from '../../selectors/newsletterSubscription/newsletterSubscription';
import {getNameSelectedMethod} from '../../selectors/paymentStep/paymentStep';
import {saveOrderDetails} from '../analytics/analytics';

const STORE_ID = 1;

/**
 * Send CheckoutInteraction event on success
 */
export function* sendPlaceOrderEvent(): SagaIterator {
    const namePaymentMethod: PAYMENTS = yield select(getNameSelectedMethod);
    const SMSPromotionStateData = yield select(getSmsSubscriptionCheckboxState);
    const paymentName = PAYMENT_CODE_MESSAGE[namePaymentMethod];

    if (!PAYMENT_CODE_MESSAGE[namePaymentMethod]) {
        logWithSentry(new Error('Payment method is not recognized in PAYMENT_CODE_MESSAGE.'), {
            paymentMethod: namePaymentMethod,
        });
    }

    const placeOrderEvent = createGAEvent(CHECKOUT_PAYMENT_STEP, {
        message: `CTA - Place Order - ${paymentName}`,
        method: 'Payment',
        version: 'D',
    });

    if (checkAmazonEvent(namePaymentMethod)) {
        yield call(pushDataLayer, {
            ...placeOrderEvent,
            EmailPromotionState: CHECKBOX_STATUS.AUTO_MARKED,
            SMSPromotionState: SMSPromotionStateData,
        });
    } else {
        yield call(pushDataLayer, {
            ...placeOrderEvent,
            SMSPromotionState: SMSPromotionStateData,
        });
    }
}

/**
 * Start generate one time coupon when flag is enabled
 */
export function* startGenerateOneTimeCoupon(orderId: string): SagaIterator {
    if (!flags.dTypOneTimeCoupon.isEnabled()) return;
    yield put(generateOneTimeCoupon({orderId}));
}

/**
 * Redirect to success page if payment request is success
 */
// eslint-disable-next-line require-jsdoc
export function* redirectToSuccessPage(): SagaIterator {
    try {
        const navigate = yield getContext('navigate');
        const orderRequest = yield select(getCompleteOrderResult);
        if (orderRequest.success || orderRequest.status === PAYPAL_ORDER_SUCCESS_STATUS) {
            yield call(startGenerateOneTimeCoupon, orderRequest.order_id);
            yield call(saveOrderDetails);
            yield call(sendPlaceOrderEvent);
            yield call(navigate, '/checkout/onepage/success/');
        } else if (orderRequest.error && orderRequest.redirect) {
            yield call(navigate, orderRequest.redirect);
        }
    } catch (error) {
        yield call([console, 'log'], error);
    }
}

/**
 * Send error event
 */
export function* sendErrorEvent(errors: OrderErrorType[]): SagaIterator {
    const namePaymentMethod: PAYMENTS = yield select(getNameSelectedMethod);
    const paymentName = PAYMENT_CODE_MESSAGE[namePaymentMethod];

    if (!PAYMENT_CODE_MESSAGE[namePaymentMethod]) {
        logWithSentry(new Error('Payment method is not recognized in PAYMENT_CODE_MESSAGE.'), {
            paymentMethod: namePaymentMethod,
        });
    }

    const placeOrderEvent = createGAEvent(
        CHECKOUT_PLACE_ORDER_PAYMENT_STEP,
        {
            message: `Error – ${errors.map((error: OrderErrorType) => error.title).join(' | ')}`,
            method: paymentName,
            version: 'D',
        },
        false,
    );

    handlePayPalDesktopErrorAnalytic({
        message: `Error – ${errors.map((error: OrderErrorType) => error.title).join(' | ')}`,
    });

    if (checkAmazonEvent(namePaymentMethod)) {
        yield call(pushDataLayer, {
            ...placeOrderEvent,
            EmailPromotionState: CHECKBOX_STATUS.AUTO_MARKED,
        });
    } else {
        yield call(pushDataLayer, placeOrderEvent);
    }
}

/**
 *  Processing errors from complete
 * @param {Action<CompleteOrderType>} - Action
 */
export function* processingErrorsFromCompleteOrder({
    payload: {
        response: {data},
    },
}: ActionType<{
    response: {
        data: {
            message: string;
            go_to?: keyof typeof STEPS;
            error: boolean;
            errors: OrderErrorType[];
        };
    };
}>): SagaIterator {
    const {errors} = data;
    const selectedPaymentMethod: PAYMENTS = yield select(getNameSelectedMethod);

    // The customer address needs to be re-saved when saveOrder fails with paypal_v3,
    // to override the empty address values that were sent for backend validation
    if (selectedPaymentMethod === PAYMENTS.METHOD_PAYPAL_V3) {
        yield put(resaveDelivery());
    }

    if (errors?.length > 0) {
        yield call(sendErrorEvent, errors);
    }
    if (data.go_to) {
        yield put(startSetCheckoutStepSaga(STEPS[data.go_to]));
    }
}

/**
 * send newsletterMs
 */
export function* sendNewsletterMs() {
    const phoneNumber: string = yield select(getShippingPhoneNumber);
    const emailData: string = yield select(getShippingEmail);
    const isSmsChecked: boolean = yield select(getSmsSubscriptionCheckboxStatus);
    const customerEmail: string = apiConfig.hostStore.getState().customer.email;

    if ((emailData || customerEmail) && phoneNumber && isSmsChecked) {
        try {
            yield call(apiConfig.instanceGusaBackend.request, {
                baseURL: apiConfig.API_URL,
                data: {
                    email: emailData || customerEmail,
                    keyword: 'checkout',
                    phone: phoneNumber,
                    storeId: STORE_ID,
                },
                method: 'post',
                url: ENDPOINTS.SMS_SUBSCRIPTION_DELAY,
            });
            yield call(cookieStore.set, SUBSCRIBER_BY_PHONE, 'true');
        } catch (error) {
            throw new Error(error);
        }
    }
}

/**
 * Begin of saga
 */
export function* placeOrderRootSaga(): SagaIterator {
    yield takeEvery(`${completeOrderReview.fulfilled}`, redirectToSuccessPage);
    yield takeEvery(
        [`${completeOrderReview.fulfilled}`, `${completeOrderReviewPaypal.fulfilled}`],
        sendNewsletterMs,
    );
    yield takeEvery(`${completeOrderReviewPaypal.fulfilled}`, redirectToSuccessPage);
    yield takeEvery(`${completeOrderReviewPaypal.rejected}`, processingErrorsFromCompleteOrder);
    yield takeEvery(`${completeOrderReview.rejected}`, processingErrorsFromCompleteOrder);
}
